// src/app/pages/qualite/qualite.component.ts
import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, computed, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ObjectifsComponent } from '../objectifs/objectifs.component';
import { RapportComponent } from '../rapport/rapport.component';
import { FormulaireComponent } from '../formulaire/formulaire.component';
import { FormsModule } from '@angular/forms';
import { MessageComponent } from '../../component/message/message.component';
import { PageSiteSwitcherComponent } from '../../component/page-site-switcher/page-site-switcher.component';

import { ActivatedRoute, Router } from '@angular/router';
import { QualityService, WeekAvailability, MonthAvailability, QualityWeekResponse, QualityMonthResponse, ParticipationWeekResponse, ParticipationMonthResponse } from '../../core/services/quality.service';
import { QualityWeek } from '../../core/api.types';
import { SessionService } from '../../core/session.service';
import { AuthService } from '../../auth/auth.service';
import type { HttpResponse } from '@angular/common/http';

type Slice = { name: string; value: number };

@Component({
  standalone: true,
  selector: 'app-qualite',
  imports: [CommonModule, FormsModule, ObjectifsComponent, RapportComponent, FormulaireComponent, MessageComponent, PageSiteSwitcherComponent, TranslatePipe],
  templateUrl: './qualite.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./qualite.component.scss'],
})
export class QualiteComponent {
  private readonly api = inject(QualityService);
  private readonly translate = inject(TranslateService);
  protected readonly session = inject(SessionService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  /**
   * Vue agrégée "tous les sites" : réservée aux clients avec le droit Waitora V2 (SUPERVISION),
   * et jamais quand `forceSingleSite` (intégration dans /site, qui reste toujours mono-site).
   * Non privée : utilisée aussi par le template pour l'affichage du nom du site et des onglets.
   */
  useOrgWideView(): boolean {
    return !this.forceSingleSite && this.auth.hasPermission('SUPERVISION');
  }

  /** Onglet "Administration qualité" et bouton "Rapport mensuel" réservés aux admins. */
  get isAdmin(): boolean {
    return this.auth.hasPermission('ADMIN');
  }

  // Flag pour activer/désactiver le mode bouchonné
  private readonly MOCK_MODE = false; // Changer à true pour activer le bouchonnage

  // 3 jeux de données bouchonnées selon l'établissement
  private getMockData(establishmentId: string, isoYear?: number, isoWeek?: number, month?: number) {
    const id = establishmentId.replace(/-/g, '');
    const firstThreeChars = id.substring(0, 3);
    const hexValue = parseInt(firstThreeChars, 16);

    let dataType: number;
    if (hexValue <= 1365) {
      dataType = 0;
    } else if (hexValue <= 2730) {
      dataType = 1;
    } else {
      dataType = 2;
    }

    let mockData: any;
    if (dataType === 0) {
      mockData = this.MOCK_DATA_GOOD;
    } else if (dataType === 1) {
      mockData = this.MOCK_DATA_AVERAGE;
    } else {
      mockData = this.MOCK_DATA_BAD;
    }

    const selectedWeek = isoWeek || 47;
    const selectedMonth = month || 11;

    return {
      qualityWeek: mockData.weeks[selectedWeek] || mockData.weeks[47],
      qualityMonth: mockData.months[selectedMonth] || mockData.months[11],
      participation: mockData.participation
    };
  }

  private readonly MOCK_DATA_GOOD = {
    months: {
      9: { feedbackAvgScore: 4.7, feedbackParticipation: 95, feedbackCount: 230, feedbackLt10: 200, feedback10_20: 25, feedbackGt20: 5, complaintRate: 0.8, equityIndex: 0.96, isoCompliance: 5, participationHistory: [90, 92, 94, 95, 96, 95, 95], complaintHistory: [1.0, 0.9, 0.8, 0.8, 0.7, 0.8, 0.8], waitingPerceptionHistory: { courte: [88, 90, 91, 93, 95, 93, 95], moyenne: [10, 8, 7, 6, 4, 6, 4], longue: [2, 2, 2, 1, 1, 1, 1] } },
      10: { feedbackAvgScore: 4.6, feedbackParticipation: 93, feedbackCount: 220, feedbackLt10: 190, feedback10_20: 25, feedbackGt20: 5, complaintRate: 0.9, equityIndex: 0.95, isoCompliance: 5, participationHistory: [88, 90, 92, 93, 94, 93, 93], complaintHistory: [1.1, 1.0, 0.9, 0.9, 0.8, 0.9, 0.9], waitingPerceptionHistory: { courte: [85, 87, 88, 90, 92, 90, 93], moyenne: [12, 10, 9, 8, 6, 8, 6], longue: [3, 3, 3, 2, 2, 2, 1] } },
      11: { feedbackAvgScore: 4.5, feedbackParticipation: 92, feedbackCount: 200, feedbackLt10: 170, feedback10_20: 25, feedbackGt20: 5, complaintRate: 1.0, equityIndex: 0.94, isoCompliance: 5, participationHistory: [87, 89, 91, 92, 93, 92, 92], complaintHistory: [1.2, 1.1, 1.0, 1.0, 0.9, 1.0, 1.0], waitingPerceptionHistory: { courte: [82, 84, 85, 87, 89, 87, 92], moyenne: [15, 13, 12, 11, 9, 11, 7], longue: [3, 3, 3, 2, 2, 2, 1] } }
    },
    weeks: {
      40: { establishmentId: 'mock-id', weekStart: '2024-09-30', isoYear: 2024, isoWeek: 40, avgMinutes: 11.8, minMinutes: 3, maxMinutes: 22, distLt10: 125, dist10_20: 185, distGt20: 25, trendVsPrev: -5.2, reliabilityOraPercent: 88, reliabilityOraMae: 2.9, reliabilitySample: 48, feedbackTotal: 42, feedbackLt10: 38, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 95, feedbackAvgScore: 4.7, perceived_wait_avg: 5.8 },
      41: { establishmentId: 'mock-id', weekStart: '2024-10-07', isoYear: 2024, isoWeek: 41, avgMinutes: 12.2, minMinutes: 3, maxMinutes: 24, distLt10: 122, dist10_20: 182, distGt20: 28, trendVsPrev: -4.8, reliabilityOraPercent: 87, reliabilityOraMae: 3.1, reliabilitySample: 46, feedbackTotal: 40, feedbackLt10: 36, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 91, feedbackAvgScore: 4.5, perceived_wait_avg: 6.1 },
      42: { establishmentId: 'mock-id', weekStart: '2024-10-14', isoYear: 2024, isoWeek: 42, avgMinutes: 11.9, minMinutes: 2, maxMinutes: 23, distLt10: 128, dist10_20: 188, distGt20: 24, trendVsPrev: -5.5, reliabilityOraPercent: 89, reliabilityOraMae: 2.8, reliabilitySample: 49, feedbackTotal: 44, feedbackLt10: 40, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 93, feedbackAvgScore: 4.7, perceived_wait_avg: 5.5 },
      43: { establishmentId: 'mock-id', weekStart: '2024-10-21', isoYear: 2024, isoWeek: 43, avgMinutes: 12.5, minMinutes: 3, maxMinutes: 25, distLt10: 120, dist10_20: 180, distGt20: 30, trendVsPrev: -4.2, reliabilityOraPercent: 86, reliabilityOraMae: 3.2, reliabilitySample: 45, feedbackTotal: 38, feedbackLt10: 34, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 89, feedbackAvgScore: 4.4, perceived_wait_avg: 6.3 },
      44: { establishmentId: 'mock-id', weekStart: '2024-10-28', isoYear: 2024, isoWeek: 44, avgMinutes: 12.0, minMinutes: 3, maxMinutes: 24, distLt10: 126, dist10_20: 184, distGt20: 26, trendVsPrev: -5.0, reliabilityOraPercent: 88, reliabilityOraMae: 3.0, reliabilitySample: 47, feedbackTotal: 41, feedbackLt10: 37, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 92, feedbackAvgScore: 4.6, perceived_wait_avg: 5.9 },
      45: { establishmentId: 'mock-id', weekStart: '2024-11-04', isoYear: 2024, isoWeek: 45, avgMinutes: 12.3, minMinutes: 3, maxMinutes: 25, distLt10: 123, dist10_20: 181, distGt20: 29, trendVsPrev: -4.5, reliabilityOraPercent: 87, reliabilityOraMae: 3.1, reliabilitySample: 46, feedbackTotal: 39, feedbackLt10: 35, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 90, feedbackAvgScore: 4.5, perceived_wait_avg: 6.2 },
      46: { establishmentId: 'mock-id', weekStart: '2024-11-11', isoYear: 2024, isoWeek: 46, avgMinutes: 11.7, minMinutes: 2, maxMinutes: 23, distLt10: 129, dist10_20: 186, distGt20: 23, trendVsPrev: -5.8, reliabilityOraPercent: 89, reliabilityOraMae: 2.7, reliabilitySample: 50, feedbackTotal: 43, feedbackLt10: 39, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 94, feedbackAvgScore: 4.7, perceived_wait_avg: 5.4 },
      47: { establishmentId: 'mock-id', weekStart: '2024-11-18', isoYear: 2024, isoWeek: 47, avgMinutes: 12.5, minMinutes: 3, maxMinutes: 25, distLt10: 120, dist10_20: 180, distGt20: 30, trendVsPrev: -4.0, reliabilityOraPercent: 86, reliabilityOraMae: 3.2, reliabilitySample: 45, feedbackTotal: 38, feedbackLt10: 34, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 92, feedbackAvgScore: 4.5, perceived_wait_avg: 6.0 },
      48: { establishmentId: 'mock-id', weekStart: '2024-11-25', isoYear: 2024, isoWeek: 48, avgMinutes: 12.1, minMinutes: 3, maxMinutes: 24, distLt10: 125, dist10_20: 183, distGt20: 27, trendVsPrev: -4.8, reliabilityOraPercent: 87, reliabilityOraMae: 3.0, reliabilitySample: 47, feedbackTotal: 40, feedbackLt10: 36, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 91, feedbackAvgScore: 4.5, perceived_wait_avg: 6.0 },
      49: { establishmentId: 'mock-id', weekStart: '2024-12-02', isoYear: 2024, isoWeek: 49, avgMinutes: 11.9, minMinutes: 2, maxMinutes: 23, distLt10: 127, dist10_20: 185, distGt20: 25, trendVsPrev: -5.2, reliabilityOraPercent: 88, reliabilityOraMae: 2.9, reliabilitySample: 48, feedbackTotal: 42, feedbackLt10: 38, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 92, feedbackAvgScore: 4.6, perceived_wait_avg: 5.7 },
      50: { establishmentId: 'mock-id', weekStart: '2024-12-09', isoYear: 2024, isoWeek: 50, avgMinutes: 12.4, minMinutes: 3, maxMinutes: 25, distLt10: 121, dist10_20: 179, distGt20: 31, trendVsPrev: -4.3, reliabilityOraPercent: 86, reliabilityOraMae: 3.3, reliabilitySample: 44, feedbackTotal: 37, feedbackLt10: 33, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 88, feedbackAvgScore: 4.3, perceived_wait_avg: 6.4 },
      51: { establishmentId: 'mock-id', weekStart: '2024-12-16', isoYear: 2024, isoWeek: 51, avgMinutes: 12.0, minMinutes: 3, maxMinutes: 24, distLt10: 126, dist10_20: 182, distGt20: 28, trendVsPrev: -4.9, reliabilityOraPercent: 87, reliabilityOraMae: 3.1, reliabilitySample: 46, feedbackTotal: 39, feedbackLt10: 35, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 90, feedbackAvgScore: 4.5, perceived_wait_avg: 5.9 },
      52: { establishmentId: 'mock-id', weekStart: '2024-12-23', isoYear: 2024, isoWeek: 52, avgMinutes: 11.6, minMinutes: 2, maxMinutes: 22, distLt10: 130, dist10_20: 187, distGt20: 22, trendVsPrev: -6.1, reliabilityOraPercent: 90, reliabilityOraMae: 2.6, reliabilitySample: 51, feedbackTotal: 44, feedbackLt10: 40, feedback10_20: 3, feedbackGt20: 1, feedbackParticipation: 95, feedbackAvgScore: 4.8, perceived_wait_avg: 5.2 }
    },
    participation: {
      week: { total_feedback: 45, total_qr: 120, overall_participation: 37.5, daily_participation: [35.0, 40.0, 38.0, 32.0, 42.0, 39.0] },
      month: { current_feedback: 250, current_qr: 600, current_participation: 41.7, monthly_history: [35.0, 38.0, 42.0] }
    }
  };

  private readonly MOCK_DATA_AVERAGE = {
    months: {
      9: { feedbackAvgScore: 3.9, feedbackParticipation: 80, feedbackCount: 170, feedbackLt10: 135, feedback10_20: 25, feedbackGt20: 10, complaintRate: 1.6, equityIndex: 0.82, isoCompliance: 3, participationHistory: [76, 78, 79, 80, 81, 80, 80], complaintHistory: [1.8, 1.7, 1.6, 1.6, 1.5, 1.6, 1.6], waitingPerceptionHistory: { courte: [68, 70, 71, 73, 75, 73, 77], moyenne: [22, 20, 19, 18, 16, 18, 15], longue: [10, 10, 10, 9, 9, 9, 8] } },
      10: { feedbackAvgScore: 3.7, feedbackParticipation: 78, feedbackCount: 150, feedbackLt10: 120, feedback10_20: 20, feedbackGt20: 10, complaintRate: 1.7, equityIndex: 0.81, isoCompliance: 3, participationHistory: [74, 76, 77, 78, 79, 78, 78], complaintHistory: [1.9, 1.8, 1.7, 1.7, 1.6, 1.7, 1.7], waitingPerceptionHistory: { courte: [65, 67, 68, 70, 72, 70, 75], moyenne: [25, 23, 22, 21, 19, 21, 17], longue: [10, 10, 10, 9, 9, 9, 8] } },
      11: { feedbackAvgScore: 3.6, feedbackParticipation: 76, feedbackCount: 140, feedbackLt10: 110, feedback10_20: 20, feedbackGt20: 10, complaintRate: 1.8, equityIndex: 0.80, isoCompliance: 3, participationHistory: [72, 74, 75, 76, 77, 76, 76], complaintHistory: [2.0, 1.9, 1.8, 1.8, 1.7, 1.8, 1.8], waitingPerceptionHistory: { courte: [62, 64, 65, 67, 69, 67, 72], moyenne: [28, 26, 25, 24, 22, 24, 20], longue: [10, 10, 10, 9, 9, 9, 8] } }
    },
    weeks: {
      40: { establishmentId: 'mock-id', weekStart: '2024-09-30', isoYear: 2024, isoWeek: 40, avgMinutes: 15.8, minMinutes: 5, maxMinutes: 34, distLt10: 71, dist10_20: 213, distGt20: 66, trendVsPrev: -4.0, reliabilityOraPercent: 75, reliabilityOraMae: 4.94, reliabilitySample: 36, feedbackTotal: 30, feedbackLt10: 28, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 78, feedbackAvgScore: 3.8, perceived_wait_avg: 13.5 },
      41: { establishmentId: 'mock-id', weekStart: '2024-10-07', isoYear: 2024, isoWeek: 41, avgMinutes: 15.2, minMinutes: 5, maxMinutes: 33, distLt10: 73, dist10_20: 210, distGt20: 67, trendVsPrev: -3.8, reliabilityOraPercent: 76, reliabilityOraMae: 4.8, reliabilitySample: 37, feedbackTotal: 31, feedbackLt10: 29, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 86, feedbackAvgScore: 4.1, perceived_wait_avg: 12.3 },
      42: { establishmentId: 'mock-id', weekStart: '2024-10-14', isoYear: 2024, isoWeek: 42, avgMinutes: 15.5, minMinutes: 4, maxMinutes: 35, distLt10: 70, dist10_20: 215, distGt20: 65, trendVsPrev: -4.2, reliabilityOraPercent: 74, reliabilityOraMae: 5.1, reliabilitySample: 35, feedbackTotal: 29, feedbackLt10: 27, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 84, feedbackAvgScore: 4.3, perceived_wait_avg: 12.7 },
      43: { establishmentId: 'mock-id', weekStart: '2024-10-21', isoYear: 2024, isoWeek: 43, avgMinutes: 15.28, minMinutes: 5, maxMinutes: 34, distLt10: 71, dist10_20: 213, distGt20: 66, trendVsPrev: -4.0, reliabilityOraPercent: 75, reliabilityOraMae: 4.94, reliabilitySample: 36, feedbackTotal: 30, feedbackLt10: 28, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 85, feedbackAvgScore: 4.2, perceived_wait_avg: 12.5 },
      44: { establishmentId: 'mock-id', weekStart: '2024-10-28', isoYear: 2024, isoWeek: 44, avgMinutes: 15.1, minMinutes: 5, maxMinutes: 33, distLt10: 72, dist10_20: 211, distGt20: 67, trendVsPrev: -3.9, reliabilityOraPercent: 76, reliabilityOraMae: 4.7, reliabilitySample: 38, feedbackTotal: 32, feedbackLt10: 30, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 87, feedbackAvgScore: 4.0, perceived_wait_avg: 12.2 },
      45: { establishmentId: 'mock-id', weekStart: '2024-11-04', isoYear: 2024, isoWeek: 45, avgMinutes: 15.4, minMinutes: 5, maxMinutes: 34, distLt10: 70, dist10_20: 214, distGt20: 66, trendVsPrev: -4.1, reliabilityOraPercent: 75, reliabilityOraMae: 4.9, reliabilitySample: 36, feedbackTotal: 30, feedbackLt10: 28, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 85, feedbackAvgScore: 4.2, perceived_wait_avg: 12.6 },
      46: { establishmentId: 'mock-id', weekStart: '2024-11-11', isoYear: 2024, isoWeek: 46, avgMinutes: 15.0, minMinutes: 4, maxMinutes: 33, distLt10: 73, dist10_20: 212, distGt20: 65, trendVsPrev: -4.3, reliabilityOraPercent: 77, reliabilityOraMae: 4.6, reliabilitySample: 39, feedbackTotal: 33, feedbackLt10: 31, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 88, feedbackAvgScore: 3.9, perceived_wait_avg: 12.1 },
      47: { establishmentId: 'mock-id', weekStart: '2024-11-18', isoYear: 2024, isoWeek: 47, avgMinutes: 15.28, minMinutes: 5, maxMinutes: 34, distLt10: 71, dist10_20: 213, distGt20: 66, trendVsPrev: -4.0, reliabilityOraPercent: 75, reliabilityOraMae: 4.94, reliabilitySample: 36, feedbackTotal: 30, feedbackLt10: 28, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 77, feedbackAvgScore: 3.7, perceived_wait_avg: 13.7 },
      48: { establishmentId: 'mock-id', weekStart: '2024-11-25', isoYear: 2024, isoWeek: 48, avgMinutes: 15.3, minMinutes: 5, maxMinutes: 34, distLt10: 71, dist10_20: 212, distGt20: 67, trendVsPrev: -3.8, reliabilityOraPercent: 75, reliabilityOraMae: 5.0, reliabilitySample: 35, feedbackTotal: 29, feedbackLt10: 27, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 84, feedbackAvgScore: 4.1, perceived_wait_avg: 12.8 },
      49: { establishmentId: 'mock-id', weekStart: '2024-12-02', isoYear: 2024, isoWeek: 49, avgMinutes: 15.1, minMinutes: 4, maxMinutes: 33, distLt10: 72, dist10_20: 214, distGt20: 64, trendVsPrev: -4.2, reliabilityOraPercent: 76, reliabilityOraMae: 4.8, reliabilitySample: 37, feedbackTotal: 31, feedbackLt10: 29, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 86, feedbackAvgScore: 4.0, perceived_wait_avg: 12.3 },
      50: { establishmentId: 'mock-id', weekStart: '2024-12-09', isoYear: 2024, isoWeek: 50, avgMinutes: 15.6, minMinutes: 5, maxMinutes: 35, distLt10: 69, dist10_20: 215, distGt20: 66, trendVsPrev: -3.7, reliabilityOraPercent: 74, reliabilityOraMae: 5.2, reliabilitySample: 34, feedbackTotal: 28, feedbackLt10: 26, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 83, feedbackAvgScore: 4.3, perceived_wait_avg: 12.9 },
      51: { establishmentId: 'mock-id', weekStart: '2024-12-16', isoYear: 2024, isoWeek: 51, avgMinutes: 15.2, minMinutes: 5, maxMinutes: 34, distLt10: 71, dist10_20: 213, distGt20: 66, trendVsPrev: -4.0, reliabilityOraPercent: 75, reliabilityOraMae: 4.9, reliabilitySample: 36, feedbackTotal: 30, feedbackLt10: 28, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 85, feedbackAvgScore: 4.2, perceived_wait_avg: 12.4 },
      52: { establishmentId: 'mock-id', weekStart: '2024-12-23', isoYear: 2024, isoWeek: 52, avgMinutes: 14.9, minMinutes: 4, maxMinutes: 32, distLt10: 74, dist10_20: 211, distGt20: 65, trendVsPrev: -4.4, reliabilityOraPercent: 77, reliabilityOraMae: 4.5, reliabilitySample: 40, feedbackTotal: 34, feedbackLt10: 32, feedback10_20: 1, feedbackGt20: 1, feedbackParticipation: 89, feedbackAvgScore: 3.8, perceived_wait_avg: 12.0 }
    },
    participation: {
      week: { total_feedback: 28, total_qr: 100, overall_participation: 28.0, daily_participation: [25.0, 30.0, 28.0, 22.0, 32.0, 29.0] },
      month: { current_feedback: 160, current_qr: 500, current_participation: 32.0, monthly_history: [28.0, 30.0, 32.0] }
    }
  };

  private readonly MOCK_DATA_BAD = {
    months: {
      9: { feedbackAvgScore: 2.8, feedbackParticipation: 62, feedbackCount: 120, feedbackLt10: 50, feedback10_20: 40, feedbackGt20: 30, complaintRate: 3.2, equityIndex: 0.68, isoCompliance: 2, participationHistory: [58, 60, 61, 62, 63, 62, 62], complaintHistory: [3.5, 3.4, 3.3, 3.2, 3.1, 3.2, 3.2], waitingPerceptionHistory: { courte: [25, 27, 28, 30, 32, 30, 35], moyenne: [35, 33, 32, 31, 29, 31, 27], longue: [40, 40, 40, 39, 39, 39, 38] } },
      10: { feedbackAvgScore: 2.6, feedbackParticipation: 60, feedbackCount: 100, feedbackLt10: 40, feedback10_20: 30, feedbackGt20: 30, complaintRate: 3.4, equityIndex: 0.66, isoCompliance: 2, participationHistory: [56, 58, 59, 60, 61, 60, 60], complaintHistory: [3.7, 3.6, 3.5, 3.4, 3.3, 3.4, 3.4], waitingPerceptionHistory: { courte: [22, 24, 25, 27, 29, 27, 32], moyenne: [38, 36, 35, 34, 32, 34, 30], longue: [40, 40, 40, 39, 39, 39, 38] } },
      11: { feedbackAvgScore: 2.5, feedbackParticipation: 58, feedbackCount: 90, feedbackLt10: 35, feedback10_20: 25, feedbackGt20: 30, complaintRate: 3.6, equityIndex: 0.64, isoCompliance: 2, participationHistory: [54, 56, 57, 58, 59, 58, 58], complaintHistory: [3.9, 3.8, 3.7, 3.6, 3.5, 3.6, 3.6], waitingPerceptionHistory: { courte: [20, 22, 23, 25, 27, 25, 30], moyenne: [40, 38, 37, 36, 34, 36, 32], longue: [40, 40, 40, 39, 39, 39, 38] } }
    },
    weeks: {
      40: { establishmentId: 'mock-id', weekStart: '2024-09-30', isoYear: 2024, isoWeek: 40, avgMinutes: 23.2, minMinutes: 8, maxMinutes: 46, distLt10: 44, dist10_20: 118, distGt20: 188, trendVsPrev: 3.8, reliabilityOraPercent: 64, reliabilityOraMae: 6.4, reliabilitySample: 27, feedbackTotal: 19, feedbackLt10: 7, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 64, feedbackAvgScore: 2.7, perceived_wait_avg: 28.8 },
      41: { establishmentId: 'mock-id', weekStart: '2024-10-07', isoYear: 2024, isoWeek: 41, avgMinutes: 22.5, minMinutes: 8, maxMinutes: 44, distLt10: 46, dist10_20: 122, distGt20: 182, trendVsPrev: 3.2, reliabilityOraPercent: 66, reliabilityOraMae: 6.0, reliabilitySample: 29, feedbackTotal: 21, feedbackLt10: 9, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 67, feedbackAvgScore: 2.9, perceived_wait_avg: 29.2 },
      42: { establishmentId: 'mock-id', weekStart: '2024-10-14', isoYear: 2024, isoWeek: 42, avgMinutes: 23.1, minMinutes: 7, maxMinutes: 47, distLt10: 43, dist10_20: 117, distGt20: 190, trendVsPrev: 4.1, reliabilityOraPercent: 63, reliabilityOraMae: 6.7, reliabilitySample: 26, feedbackTotal: 18, feedbackLt10: 6, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 62, feedbackAvgScore: 2.6, perceived_wait_avg: 19.1 },
      43: { establishmentId: 'mock-id', weekStart: '2024-10-21', isoYear: 2024, isoWeek: 43, avgMinutes: 22.8, minMinutes: 8, maxMinutes: 45, distLt10: 45, dist10_20: 120, distGt20: 185, trendVsPrev: 3.5, reliabilityOraPercent: 65, reliabilityOraMae: 6.2, reliabilitySample: 28, feedbackTotal: 20, feedbackLt10: 8, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 65, feedbackAvgScore: 2.8, perceived_wait_avg: 18.5 },
      44: { establishmentId: 'mock-id', weekStart: '2024-10-28', isoYear: 2024, isoWeek: 44, avgMinutes: 22.3, minMinutes: 8, maxMinutes: 43, distLt10: 47, dist10_20: 123, distGt20: 180, trendVsPrev: 3.0, reliabilityOraPercent: 67, reliabilityOraMae: 5.8, reliabilitySample: 30, feedbackTotal: 22, feedbackLt10: 10, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 68, feedbackAvgScore: 3.0, perceived_wait_avg: 17.9 },
      45: { establishmentId: 'mock-id', weekStart: '2024-11-04', isoYear: 2024, isoWeek: 45, avgMinutes: 22.9, minMinutes: 8, maxMinutes: 45, distLt10: 44, dist10_20: 119, distGt20: 187, trendVsPrev: 3.6, reliabilityOraPercent: 64, reliabilityOraMae: 6.3, reliabilitySample: 27, feedbackTotal: 19, feedbackLt10: 7, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 63, feedbackAvgScore: 2.7, perceived_wait_avg: 18.7 },
      46: { establishmentId: 'mock-id', weekStart: '2024-11-11', isoYear: 2024, isoWeek: 46, avgMinutes: 22.1, minMinutes: 7, maxMinutes: 42, distLt10: 48, dist10_20: 125, distGt20: 177, trendVsPrev: 2.8, reliabilityOraPercent: 68, reliabilityOraMae: 5.5, reliabilitySample: 32, feedbackTotal: 24, feedbackLt10: 12, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 71, feedbackAvgScore: 3.2, perceived_wait_avg: 17.6 },
      47: { establishmentId: 'mock-id', weekStart: '2024-11-18', isoYear: 2024, isoWeek: 47, avgMinutes: 22.8, minMinutes: 8, maxMinutes: 45, distLt10: 45, dist10_20: 120, distGt20: 185, trendVsPrev: 3.5, reliabilityOraPercent: 65, reliabilityOraMae: 6.2, reliabilitySample: 28, feedbackTotal: 20, feedbackLt10: 8, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 65, feedbackAvgScore: 2.8, perceived_wait_avg: 28.5 },
      48: { establishmentId: 'mock-id', weekStart: '2024-11-25', isoYear: 2024, isoWeek: 48, avgMinutes: 22.6, minMinutes: 8, maxMinutes: 44, distLt10: 46, dist10_20: 121, distGt20: 183, trendVsPrev: 3.3, reliabilityOraPercent: 66, reliabilityOraMae: 6.1, reliabilitySample: 28, feedbackTotal: 20, feedbackLt10: 8, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 66, feedbackAvgScore: 2.9, perceived_wait_avg: 18.3 },
      49: { establishmentId: 'mock-id', weekStart: '2024-12-02', isoYear: 2024, isoWeek: 49, avgMinutes: 22.4, minMinutes: 7, maxMinutes: 43, distLt10: 47, dist10_20: 123, distGt20: 180, trendVsPrev: 3.1, reliabilityOraPercent: 67, reliabilityOraMae: 5.9, reliabilitySample: 29, feedbackTotal: 21, feedbackLt10: 9, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 67, feedbackAvgScore: 3.0, perceived_wait_avg: 18.0 },
      50: { establishmentId: 'mock-id', weekStart: '2024-12-09', isoYear: 2024, isoWeek: 50, avgMinutes: 23.0, minMinutes: 8, maxMinutes: 46, distLt10: 43, dist10_20: 118, distGt20: 189, trendVsPrev: 3.9, reliabilityOraPercent: 63, reliabilityOraMae: 6.5, reliabilitySample: 26, feedbackTotal: 18, feedbackLt10: 6, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 61, feedbackAvgScore: 2.6, perceived_wait_avg: 19.0 },
      51: { establishmentId: 'mock-id', weekStart: '2024-12-16', isoYear: 2024, isoWeek: 51, avgMinutes: 22.7, minMinutes: 8, maxMinutes: 45, distLt10: 45, dist10_20: 120, distGt20: 185, trendVsPrev: 3.4, reliabilityOraPercent: 65, reliabilityOraMae: 6.2, reliabilitySample: 28, feedbackTotal: 20, feedbackLt10: 8, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 65, feedbackAvgScore: 2.8, perceived_wait_avg: 18.4 },
      52: { establishmentId: 'mock-id', weekStart: '2024-12-23', isoYear: 2024, isoWeek: 52, avgMinutes: 22.0, minMinutes: 7, maxMinutes: 41, distLt10: 49, dist10_20: 126, distGt20: 175, trendVsPrev: 2.6, reliabilityOraPercent: 69, reliabilityOraMae: 5.3, reliabilitySample: 33, feedbackTotal: 25, feedbackLt10: 13, feedback10_20: 5, feedbackGt20: 7, feedbackParticipation: 73, feedbackAvgScore: 3.3, perceived_wait_avg: 27.3 }
    },
    participation: {
      week: { total_feedback: 18, total_qr: 90, overall_participation: 20.0, daily_participation: [15.0, 22.0, 18.0, 12.0, 25.0, 20.0] },
      month: { current_feedback: 95, current_qr: 450, current_participation: 21.1, monthly_history: [18.0, 19.0, 21.0] }
    }
  };

  private readonly MOCK_DATA_BASE = this.MOCK_DATA_AVERAGE;

  year = signal<number>(new Date().getFullYear());
  week = signal<number>(1);
  month = signal<number>(new Date().getMonth() + 1);


  // Ouverture des panneaux custom
  yearOpen = signal<boolean>(false);
  weekOpen = signal<boolean>(false);
  monthOpen = signal<boolean>(false);
  periodMode = signal<'week' | 'month'>('month');

  loading = signal<boolean>(true);
  error = signal<string | null>(null);
  data = signal<QualityWeek | null>(null);
  qualityData = signal<QualityWeekResponse | QualityMonthResponse | null>(null);
  satisfactionData = signal<{avg_satisfaction: number, satisfaction_count: number} | null>(null);
  participationData = signal<ParticipationWeekResponse | ParticipationMonthResponse | null>(null);
  analyticsData = signal<any>(null);
  private fetchInProgress = false;
  private pendingOperations = 0;
  private currentEstablishmentId = '';
  @Input() inline = false;
  // Distinct de `inline` : `inline` ne fait que masquer le chrome (fil d'Ariane des
  // sous-pages) d'une intégration dans une autre page. `forceSingleSite` force en plus
  // le mode mono-site quel que soit le droit SUPERVISION — nécessaire pour l'intégration
  // dans /site, qui doit toujours rester sur l'établissement sélectionné (voir
  // useOrgWideView ci-dessous). Ne pas positionner ce flag pour une intégration qui doit
  // au contraire profiter de la vue agrégée tous-sites (ex: /statistiques).
  @Input() forceSingleSite = false;

  private loadingAvailability = new Set<string>();
  showSatisfactionSubpage = signal<boolean>(false);
  showObjectifsSubpage = signal<boolean>(false);
  showRapportSubpage = signal<boolean>(false);
  showCommunicationSubpage = signal<boolean>(false);
  showFormulaireSubpage = signal<boolean>(false);
  isRefreshing = signal(false);
  activeTab = signal<'objectifs' | 'communication' | 'formulaire'>('objectifs');
  messageRapide = '';

  /** Map "semaine -> hasData" pour l'année sélectionnée */
  private weekHasData = signal<Record<number, boolean>>({});
  /** Map "mois -> hasData" pour l'année sélectionnée */
  private monthHasData = signal<Record<number, boolean>>({});

  constructor() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    
    this.year.set(currentYear);
    this.week.set(1);
    this.month.set(currentMonth);
    
    this.route.queryParamMap.subscribe((q) => {
      const y = Number(q.get('year'));
      const w = Number(q.get('week') ?? q.get('isoWeek'));
      
      if (Number.isFinite(y) && y >= 2020 && y <= 2030) {
        this.year.set(y);
      }
      if (Number.isFinite(w) && w >= 1 && w <= 53) {
        this.week.set(w);
      }
    });

    const initialEst = this.session.selectedId();
    if (initialEst) {
      this.loadEstablishmentData(initialEst);
    }

    effect(() => {
      const est = this.session.selectedId();
      if (est && est !== this.currentEstablishmentId) {
        this.resetAllData();
        this.currentEstablishmentId = est;
        setTimeout(() => {
          if (this.currentEstablishmentId === est) {
            this.loadEstablishmentData(est);
          }
        }, 50);
      }
    }, { allowSignalWrites: true });
  }

  /* ======= Dropdown logic ======= */
  toggleYear(e?: Event){ e?.stopPropagation(); this.yearOpen.set(!this.yearOpen()); this.weekOpen.set(false); }
  toggleWeek(e?: Event){ e?.stopPropagation(); this.weekOpen.set(!this.weekOpen()); }
  toggleMonth(e?: Event){ e?.stopPropagation(); this.monthOpen.set(!this.monthOpen()); }

  setPeriodMode(mode: 'week' | 'month') {
    this.periodMode.set(mode);
    this.weekOpen.set(false);
    this.monthOpen.set(false);
    // Recharger les données pour le nouveau mode
    const est = this.session.selectedId();
    if (est) {
      this.fetch(est, this.year(), this.week(), this.month());
    }
  }

  currentMonth() { return this.month(); }
  currentMonthLabel() {
    return this.translate.instant('common.months.longByIndex.' + (this.month() - 1));
  }

  months() {
    const monthsData = this.monthHasData();
    // Si aucune donnée disponible, retourner liste vide
    if (!monthsData || Object.keys(monthsData).length === 0) return [];

    const allMonths = [
      { value: 1, label: 'Janvier' }, { value: 2, label: 'Février' }, { value: 3, label: 'Mars' },
      { value: 4, label: 'Avril' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juin' },
      { value: 7, label: 'Juillet' }, { value: 8, label: 'Août' }, { value: 9, label: 'Septembre' },
      { value: 10, label: 'Octobre' }, { value: 11, label: 'Novembre' }, { value: 12, label: 'Décembre' }
    ];

    return allMonths.filter(m => monthsData[m.value] === true).reverse();
  }

  selectMonth(month: number) {
    if (!this.isMonthEnabled(month)) return;
    this.month.set(month);
    // Recharger les données avec le nouveau mois
    const est = this.session.selectedId();
    if (est) {
      // Réinitialiser seulement les données, pas les listes de disponibilité
      this.data.set(null);
      this.qualityData.set(null);
      this.participationData.set(null);
      this.analyticsData.set(null);
      this.fetch(est, this.year(), this.week(), month);
    }
    this.monthOpen.set(false);
  }

  selectYear(y: number){
    if (this.year() !== y){
      this.year.set(y);
      const est = this.session.selectedId();
      if (est) {
        // Vider seulement les données, pas le cache de disponibilité
        this.data.set(null);
        this.qualityData.set(null);
        this.participationData.set(null);
        this.analyticsData.set(null);
        this.weekHasData.set({});
        this.monthHasData.set({});
        this.loading.set(true);
        this.error.set(null);
        this.loadEstablishmentData(est);
      }
    }
    this.yearOpen.set(false);
  }
  selectWeek(w: number){
    if (!this.isWeekEnabled(w)) return;
    if (this.week() !== w){
      this.week.set(w);
      const est = this.session.selectedId();
      if (est) {
        this.fetch(est, this.year(), w, this.month());
      }
    }
    this.weekOpen.set(false);
  }

  private loadOrgMonthAvailability(year: number) {
    this.api.getOrgMonthsAvailability(year).subscribe({
      next: (list: MonthAvailability[]) => {
        const map: Record<number, boolean> = {};
        for (const item of list) map[item.month] = !!item.hasData;
        this.monthHasData.set(map);
        if (Object.keys(map).length === 0) return;
        const current = this.month();
        if (!map[current]) {
          const available = Object.keys(map).filter(k => map[Number(k)]).map(Number);
          if (available.length > 0) this.month.set(Math.max(...available));
        }
      },
      error: () => { this.monthHasData.set({}); }
    });
  }

  private loadOrgWeekAvailability(year: number) {
    this.api.getOrgWeeksAvailability(year).subscribe({
      next: (list: WeekAvailability[]) => {
        const map: Record<number, boolean> = {};
        for (const item of list) map[item.isoWeek] = !!item.hasData;
        this.weekHasData.set(map);
        if (Object.keys(map).length === 0) return;
        const current = this.week();
        if (!map[current]) {
          const available = Object.keys(map).filter(k => map[Number(k)]).map(Number);
          if (available.length > 0) this.week.set(Math.max(...available));
        }
      },
      error: () => { this.weekHasData.set({}); }
    });
  }

  private loadWeekAvailability(establishmentId: string, isoYear: number){
    const key = `${establishmentId}-${isoYear}`;
    if (this.loadingAvailability.has(key)) {
      return;
    }

    this.loadingAvailability.add(key);

    if (this.MOCK_MODE) {
      const map: Record<number, boolean> = {};
      for (let week = 40; week <= 52; week++) {
        map[week] = true;
      }
      this.weekHasData.set(map);
      const current = this.week();
      if (!map[current]) {
        const availableWeeks = Object.keys(map).map(Number).sort((a, b) => b - a);
        if (availableWeeks.length > 0) {
          this.week.set(availableWeeks[0]);
        }
      }
      this.loadingAvailability.delete(key);
      return;
    }

    this.api.getEstablishmentWeeksAvailability(establishmentId, isoYear).subscribe({
      next: (list: WeekAvailability[]) => {
        const map: Record<number, boolean> = {};
        for (const item of list) map[item.isoWeek] = !!item.hasData;
        this.weekHasData.set(map);

        if (Object.keys(map).length === 0) {
          this.loadingAvailability.delete(key);
          return;
        }

        const currentWeek = this.currentIsoWeek();
        const availableWeeks = Object.keys(map).filter(k => map[Number(k)]).map(Number);
        
        let preferredWeek;
        if (availableWeeks.includes(currentWeek)) {
          preferredWeek = currentWeek;
        } else {
          preferredWeek = currentWeek;
        }

        if (this.week() !== preferredWeek) {
          this.week.set(preferredWeek);
        }
        this.loadingAvailability.delete(key);
      },
      error: () => {
        this.weekHasData.set({});
        this.loadingAvailability.delete(key);
      }
    });
  }

  /** Charge la liste des mois disponibles et ajuste le mois sélectionné si nécessaire */
  private loadMonthAvailability(establishmentId: string, year: number){
    if (this.MOCK_MODE) {
      const map: Record<number, boolean> = {};
      const currentMonth = new Date().getMonth() + 1;
      for (let i = 0; i < 3; i++) {
        let month = currentMonth - i;
        if (month <= 0) month += 12;
        map[month] = true;
      }
      this.monthHasData.set(map);
      const current = this.month();
      if (!map[current]) {
        const availableMonths = Object.keys(map).map(Number).sort((a, b) => b - a);
        if (availableMonths.length > 0) {
          this.month.set(availableMonths[0]);
        }
      }
      return;
    }

    if (!Number.isFinite(year) || year < 2020 || year > 2030) {
      this.monthHasData.set({});
      return;
    }

    this.api.getEstablishmentMonthsAvailability(establishmentId, year).subscribe({
      next: (list: MonthAvailability[]) => {
        const map: Record<number, boolean> = {};
        for (const item of list) map[item.month] = !!item.hasData;
        this.monthHasData.set(map);
        
        if (Object.keys(map).length === 0) {
          return;
        }
        
        const current = this.month();
        if (!map[current]) {
          const currentMonth = new Date().getMonth() + 1;
          const availableMonths = Object.keys(map).filter(k => map[Number(k)]).map(Number);
          if (availableMonths.length > 0) {
            let preferredMonth;
            if (availableMonths.includes(currentMonth)) {
              preferredMonth = currentMonth;
            } else {
              preferredMonth = Math.max(...availableMonths);
            }
            this.month.set(preferredMonth);
          }
        }
      },
      error: () => {
        this.monthHasData.set({});
      }
    });
  }

  /** Charge les données de participation selon le mode période */
  private loadParticipationData(establishmentId: string, isoYear: number, isoWeek: number, month?: number) {
    if (this.MOCK_MODE) {
      const mockData = this.getMockData(establishmentId, isoYear, isoWeek, month);
      const weekVariation = Math.sin(isoWeek * 0.3) * 5;
      const monthVariation = Math.cos((month || 11) * 0.4) * 3;
      const totalVariation = weekVariation + monthVariation;

      if (this.periodMode() === 'week') {
        const weekData = { ...mockData.participation.week };
        weekData.overall_participation = Math.max(0, Math.min(100, weekData.overall_participation + totalVariation));
        const baseDailyRate = weekData.overall_participation;
        weekData.daily_participation = Array.from({ length: 6 }, (_, dayIndex) => {
          const dayVariation = Math.sin((isoWeek * 7 + dayIndex) * 0.4) * 8;
          const weeklyPattern = Math.cos(dayIndex * 1.2 + isoWeek * 0.2) * 6;
          return Math.max(5, Math.min(95, baseDailyRate + dayVariation + weeklyPattern));
        });
        this.participationData.set(weekData as any);
      } else {
        const monthData = { ...mockData.participation.month };
        monthData.current_participation = Math.max(0, Math.min(100, monthData.current_participation + totalVariation));
        this.participationData.set(monthData as any);
      }
      return;
    }

    if (this.periodMode() === 'week') {
      if (!Number.isFinite(isoYear) || !Number.isFinite(isoWeek)) {
        this.participationData.set(null);
        return;
      }
      
      this.api.getEstablishmentWeekParticipation(establishmentId, isoYear, isoWeek).subscribe({
        next: (d) => { this.participationData.set(d); },
        error: () => { this.participationData.set(null); }
      });
    } else {
      const selectedMonth = month || this.month();
      
      if (!Number.isFinite(isoYear) || !Number.isFinite(selectedMonth) || selectedMonth < 1 || selectedMonth > 12) {
        this.participationData.set(null);
        return;
      }
      
      this.api.getEstablishmentMonthParticipation(establishmentId, isoYear, selectedMonth).subscribe({
        next: (d) => { this.participationData.set(d); },
        error: () => { this.participationData.set(null); }
      });
    }
  }

  /** Semaine activable ? (si map vide => tout activé) */
  isWeekEnabled(w: number): boolean {
    const map = this.weekHasData();
    // si aucune info (ex: erreur réseau), ne pas brider la navigation
    if (!map || Object.keys(map).length === 0) return true;
    return !!map[w];
  }

  /** Mois activable ? (si map vide => tout activé) */
  isMonthEnabled(m: number): boolean {
    const map = this.monthHasData();
    if (!map || Object.keys(map).length === 0) return true;
    return !!map[m];
  }

  // Valeur numérique arrondie (min) : négatif = mieux
  trendDelta = computed<number | null>(() => {
    const w = this.data();
    if (!w || w.trendVsPrev == null) return null;
    return Math.round(w.trendVsPrev);
  });

  // Classe d’affichage (couleur)
  trendClass = computed<'positive' | 'negative' | 'neutral'>(() => {
    const v = this.trendDelta();
    if (v === null || v === 0) return 'neutral';
    return v < 0 ? 'negative' : 'positive';
  });

  // ===== Satisfaction moyenne (1 décimale)
  avgScore = computed<number | null>(() => {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.satisfactionAvg != null) {
      const s = Math.max(0, Math.min(5, Number(analytics.globalMetrics.satisfactionAvg)));
      return Math.round(s * 10) / 10;
    }

    // En mode mois, utiliser les données qualité mensuelles si disponibles
    if (this.periodMode() === 'month') {
      const q = this.qualityData();
      if (q && q.feedbackAvgScore != null) {
        const s = Math.max(0, Math.min(5, Number(q.feedbackAvgScore)));
        return Math.round(s * 10) / 10;
      }
      return null;
    }

    // En mode semaine, utiliser les données legacy
    const w = this.data();
    if (w && w.feedbackAvgScore != null) {
      const s = Math.max(0, Math.min(5, Number(w.feedbackAvgScore)));
      return Math.round(s * 10) / 10;
    }
    return null;
  });

  /** Objectif configurable (maquette = 80% sous un seuil) */
  objectiveGoal = signal<number>(80);
  /** Libellé du seuil (maquette = 15 min ; tu peux le passer à '20 min') */
  objectiveThresholdLabel = signal<string>('15 min');

  /** Pourcentage total qui respecte l’objectif (ici: <20 min par défaut) */
  objectiveRate = computed(() => {
    const total = this.totalPoints(); const w = this.data();
    if (!w || !total) return 0;
    // règle actuelle (= <20 min). Change si tu passes à 15 min.
    const ok = w.distLt10 + w.dist10_20;
    return Math.round((ok / total) * 100);
  });

  objectiveMet = computed(() => this.objectiveRate() >= this.objectiveGoal());

  /** Pourcentages par tranche pour la légende */
  percentages = computed(() => {
    const w = this.data(); const total = this.totalPoints();
    if (!w || !total) return { lt10: 0, m10_20: 0, gt20: 0 };
    const pct = (v: number) => Math.round((v / total) * 100);
    return {
      lt10: pct(w.distLt10),
      m10_20: pct(w.dist10_20),
      gt20: pct(w.distGt20),
    };
  });

  /** Angles cumulés pour chaque part du camembert (en radians) */
  sliceAngles = computed(() => {
    const w = this.data(); const total = this.totalPoints();
    if (!w || !total) return [];
    const vals = [w.distLt10, w.dist10_20, w.distGt20];
    let acc = -Math.PI / 2; // départ en haut
    return vals.map(v => {
      const angle = (v / total) * Math.PI * 2;
      const start = acc;
      const end = acc + angle;
      acc = end;
      return { start, end, v };
    });
  });

  /** % de participation, arrondi, ou null si non dispo */
  participationRate = computed<number | null>(() => {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.participationRate != null) {
      return Math.round(analytics.globalMetrics.participationRate);
    }

    const p = this.participationData();
    if (p) {
      if ('overall_participation' in p && p.overall_participation > 0) {
        return Math.round(p.overall_participation);
      }
      if ('current_participation' in p && p.current_participation > 0) {
        return Math.round(p.current_participation);
      }
      if ('current_feedback' in p && 'current_qr' in p && p.current_qr > 0) {
        const rate = (p.current_feedback / p.current_qr) * 100;
        return Math.round(rate);
      }
    }

    const q = this.qualityData();
    if (q && q.feedbackParticipation != null && q.feedbackParticipation > 0) {
      return Math.round(q.feedbackParticipation);
    }
    const w = this.data();
    if (w && w.feedbackParticipation != null && w.feedbackParticipation > 0) {
      return Math.round(w.feedbackParticipation);
    }

    return null;
  });

  /** Taux réclamation */
  complaintRate = computed<number>(() => {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.complaintRate != null) {
      return Math.round(analytics.globalMetrics.complaintRate * 10) / 10;
    }

    // Mode mois : utiliser le taux pré-calculé
    if (this.periodMode() === 'month') {
      const q = this.qualityData();
      return q?.complaintRate ?? 0;
    }

    // Mode semaine : calculer à partir des feedbacks
    const w = this.data();
    if (w && w.feedbackTotal > 0) {
      const rate = (w.feedbackLt10 / w.feedbackTotal) * 100;
      return Math.round(rate * 10) / 10;
    }

    return 0;
  });

  /** Index équité d'accès (0-1) */
  equityIndex = computed<number>(() => {
    const q = this.qualityData();
    return q?.equityIndex ?? 0;
  });

  /** Conformité ISO (0-5) */
  isoCompliance = computed<number>(() => {
    const q = this.qualityData();
    return q?.isoCompliance ?? 0;
  });

  /** Historiques pour graphiques */
  participationHistory = computed<number[]>(() => {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (this.periodMode() === 'month' && analytics?.monthlyBreakdown) {
      return analytics.monthlyBreakdown.map((week: any) => week.participationRate || 0);
    }
    if (this.periodMode() === 'week' && analytics?.dailyBreakdown) {
      // Exclure le dimanche (dernier jour) - prendre les 6 premiers jours
      return analytics.dailyBreakdown.slice(0, 6).map((day: any) => day.participationRate || 0);
    }

    const p = this.participationData();
    if (p) {
      if ('daily_participation' in p) {
        // Mode semaine : 6 valeurs maximum (sans dimanche)
        const daily = p.daily_participation || [];
        return daily.slice(0, 6);
      }
      if ('monthly_history' in p) {
        // Mode mois : extraire les 3 derniers mois
        return this.extractLast3Months(p.monthly_history || []);
      }
    }

    // Fallback sur les données qualité
    const q = this.qualityData();
    if (this.periodMode() === 'month') {
      // Mode mois : extraire les 3 derniers mois
      return this.extractLast3Months(q?.participationHistory ?? []);
    } else {
      // Mode semaine : prendre les 6 premières valeurs
      return q?.participationHistory?.slice(0, 6) ?? [];
    }
  });

  complaintHistory = computed<number[]>(() => {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (this.periodMode() === 'month' && analytics?.monthlyBreakdown) {
      return analytics.monthlyBreakdown.map((week: any) => week.complaintRate || 0);
    }
    if (this.periodMode() === 'week' && analytics?.dailyBreakdown) {
      // Exclure le dimanche (dernier jour) - prendre les 6 premiers jours
      return analytics.dailyBreakdown.slice(0, 6).map((day: any) => day.complaintRate || 0);
    }

    // Fallback sur les données qualité
    const q = this.qualityData();
    if (this.periodMode() === 'week') {
      return q?.complaintHistory?.slice(0, 6) ?? [];
    } else {
      // Mode mois : extraire les 3 derniers mois
      return this.extractLast3Months(q?.complaintHistory ?? []);
    }
  });

  waitingPerceptionHistory = computed(() => {
    const q = this.qualityData();
    if (q?.waitingPerceptionHistory) {
      console.log('Waiting perception data:', q.waitingPerceptionHistory);
      return q.waitingPerceptionHistory;
    }
    return {
      courte: new Array(12).fill(0),
      moyenne: new Array(12).fill(0),
      longue: new Array(12).fill(0)
    };
  });

  waitingChartAngles = computed(() => {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (analytics?.waitPerceptionDistribution) {
      const dist = analytics.waitPerceptionDistribution;
      return {
        courte: (dist.courte / 100) * 360,
        moyenne: (dist.moyenne / 100) * 360,
        longue: (dist.longue / 100) * 360
      };
    }

    // Mode mois : utiliser les données historiques
    if (this.periodMode() === 'month') {
      const history = this.waitingPerceptionHistory();
      const currentMonth = this.month() - 1;

      if (history.courte && history.courte.length > currentMonth) {
        const courte = history.courte[currentMonth] || 0;
        const moyenne = history.moyenne[currentMonth] || 0;
        const longue = history.longue[currentMonth] || 0;

        if (courte === 0 && moyenne === 0 && longue === 0) {
          return { courte: 0, moyenne: 0, longue: 0 };
        }

        return {
          courte: (courte / 100) * 360,
          moyenne: (moyenne / 100) * 360,
          longue: (longue / 100) * 360
        };
      }
    }

    // Mode semaine : utiliser les données de feedback
    const w = this.data();
    if (!w || w.feedbackTotal <= 0) {
      return { courte: 0, moyenne: 0, longue: 0 };
    }

    const total = w.feedbackTotal;
    return {
      courte: (w.feedbackLt10 / total) * 360,
      moyenne: (w.feedback10_20 / total) * 360,
      longue: (w.feedbackGt20 / total) * 360
    };
  });

  waitingPercentages = computed(() => {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (analytics?.waitPerceptionDistribution) {
      const dist = analytics.waitPerceptionDistribution;
      return {
        courte: Math.round(dist.courte || 0),
        moyenne: Math.round(dist.moyenne || 0),
        longue: Math.round(dist.longue || 0)
      };
    }

    // Mode mois : utiliser les données historiques
    if (this.periodMode() === 'month') {
      const history = this.waitingPerceptionHistory();
      const currentMonth = this.month() - 1;

      if (history.courte && history.courte.length > currentMonth) {
        const courte = history.courte[currentMonth] || 0;
        const moyenne = history.moyenne[currentMonth] || 0;
        const longue = history.longue[currentMonth] || 0;

        if (courte === 0 && moyenne === 0 && longue === 0) {
          return { courte: 0, moyenne: 0, longue: 0 };
        }

        return {
          courte: Math.round(courte),
          moyenne: Math.round(moyenne),
          longue: Math.round(longue)
        };
      }
    }

    // Mode semaine : utiliser les données de feedback
    const w = this.data();
    if (!w || w.feedbackTotal <= 0) {
      return { courte: 0, moyenne: 0, longue: 0 };
    }

    const total = w.feedbackTotal;
    return {
      courte: Math.round((w.feedbackLt10 / total) * 100),
      moyenne: Math.round((w.feedback10_20 / total) * 100),
      longue: Math.round((w.feedbackGt20 / total) * 100)
    };
  });

  waitingCenterText = computed(() => {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.perceivedWaitMinutes != null) {
      return Math.round(analytics.globalMetrics.perceivedWaitMinutes) + ' min';
    }

    const w = this.data();
    const q = this.qualityData();

    // Mode semaine : utiliser perceivedWaitAvg
    if (this.periodMode() === 'week' && w && w.perceivedWaitAvg != null) {
      return Math.round(w.perceivedWaitAvg) + ' min';
    }

    // Mode mois : utiliser les données mensuelles si disponibles
    if (this.periodMode() === 'month' && q && 'perceivedWaitAvg' in q && (q as any).perceivedWaitAvg != null) {
      return Math.round((q as any).perceivedWaitAvg) + ' min';
    }

    return 'n/a';
  });

  /** Délais de réponse moyen en heures */
  responseTime = computed<number | null>(() => {
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.avgResponseTimeHours != null) {
      return Math.round(analytics.globalMetrics.avgResponseTimeHours * 100) / 100;
    }
    return null;
  });

  /** Affichage formaté des délais de réponse */
  getResponseTimeDisplay(): string | null {
    const time = this.responseTime();
    if (time == null || time === 0) return null;

    if (time < 1) {
      // Moins d'1h = afficher en minutes
      const minutes = Math.round(time * 60);
      return `${minutes} min`;
    } else if (time < 24) {
      // Moins d'1j = afficher en heures
      const hours = Math.floor(time);
      const minutes = Math.round((time - hours) * 60);
      return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
    } else if (time < 168) {
      // Moins d'1 semaine = afficher en jours
      const days = Math.round(time / 24 * 10) / 10;
      return days % 1 === 0 ? `${Math.round(days)}j` : `${days}j`;
    } else {
      // 1 semaine ou plus = afficher en semaines
      const weeks = Math.round(time / 168 * 10) / 10;
      return weeks % 1 === 0 ? `${Math.round(weeks)} sem` : `${weeks} sem`;
    }
  }

  /** Nombre de scans QR Code */
  scanCount = computed<number | null>(() => {
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.totalScans != null) {
      return analytics.globalMetrics.totalScans;
    }
    
    const p = this.participationData();
    if (p) {
      if ('total_qr' in p) {
        return p.total_qr;
      }
      if ('current_qr' in p) {
        return p.current_qr;
      }
    }
    return null;
  });

  getScanBadgeClass() {
    const count = this.scanCount();
    if (count == null) return '';
    if (this.periodMode() === 'month') {
      return count >= 40 ? 'excellence' : count >= 20 ? 'good' : 'warning';
    }
    return count >= 20 ? 'excellence' : count >= 10 ? 'good' : 'warning';
  }

  getScanBadgeText() {
    const count = this.scanCount();
    if (count == null) return '';
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    if (this.periodMode() === 'month') {
      return count >= 40 ? t('excellent') : count >= 20 ? t('good') : t('needsImprovement');
    }
    return count >= 20 ? t('excellent') : count >= 10 ? t('good') : t('needsImprovement');
  }

  /** Nombre d'actions */
  actionCount = computed<number | null>(() => {
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.actionsCount != null) {
      return analytics.globalMetrics.actionsCount;
    }
    return null;
  });

  getActionBadgeClass() {
    const count = this.actionCount();
    if (count == null) return '';
    return count >= 3 ? 'excellence' : count >= 1 ? 'good' : 'warning';
  }

  getActionBadgeText() {
    const count = this.actionCount();
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    if (count == null) return t('inDevelopment');
    return count >= 3 ? t('excellent') : count >= 1 ? t('good') : t('needsImprovement');
  }

  getResponseTimeBadgeClass() {
    const time = this.responseTime();
    if (time == null || time === 0) return '';
    return time <= 24 ? 'excellence' : time <= 48 ? 'good' : time <= 72 ? 'warning' : 'critical';
  }

  getResponseTimeBadgeText() {
    const time = this.responseTime();
    if (time == null || time === 0) return '';
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    return time <= 24 ? t('excellent') : time <= 48 ? t('good') : time <= 72 ? t('needsImprovement') : t('critical');
  }

  // Méthodes pour badges dynamiques
  getSatisfactionBadgeClass() {
    const score = this.avgScore();
    if (!score) return '';
    return score >= 4.0 ? 'good' : score >= 3.0 ? 'warning' : 'critical';
  }

  getSatisfactionBadgeText() {
    const score = this.avgScore();
    if (!score) return '';
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    return score >= 4.0 ? t('good') : score >= 3.0 ? t('average') : t('low');
  }

  getFeedbackCount() {
    const q = this.qualityData();
    if (q && 'feedbackCount' in q) {
      return q.feedbackCount ?? 0;
    }
    return 0;
  }

  getParticipationBadgeClass() {
    const rate = this.participationRate();
    if (!rate) return '';
    return rate > 40 ? 'excellence' : rate >= 10 ? 'good' : 'critical';
  }

  getParticipationBadgeText() {
    const rate = this.participationRate();
    if (!rate) return '';
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    return rate > 40 ? t('excellent') : rate >= 10 ? t('compliant') : t('toMonitor');
  }

  getComplaintBadgeClass() {
    const rate = this.complaintRate();
    if (rate === 0) return '';
    return rate <= 2.0 ? 'good' : rate <= 3.0 ? 'warning' : 'critical';
  }

  getComplaintBadgeText() {
    const rate = this.complaintRate();
    if (rate === 0) return '';
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    return rate <= 2.0 ? t('compliant') : rate <= 3.0 ? t('toMonitor') : t('critical');
  }

  getEquityBadgeClass() {
    const index = this.equityIndex();
    if (index === 0) return '';
    return index >= 0.8 ? 'good' : index >= 0.7 ? 'warning' : 'critical';
  }

  getEquityBadgeText() {
    const index = this.equityIndex();
    if (index === 0) return '';
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    return index >= 0.8 ? t('compliant') : index >= 0.7 ? t('toMonitor') : t('fragile');
  }

  getIsoBadgeClass() {
    const compliance = this.isoCompliance();
    if (compliance === 0) return '';
    return compliance >= 4 ? 'excellence' : compliance >= 3 ? 'good' : 'warning';
  }

  getIsoBadgeText() {
    const compliance = this.isoCompliance();
    if (compliance === 0) return '';
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    return compliance >= 4 ? t('excellence') : compliance >= 3 ? t('compliant') : t('needsImprovement');
  }

  getWaitingPerceptionText() {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.waitPerceptionAvg) {
      return analytics.globalMetrics.waitPerceptionAvg;
    }

    const w = this.data();
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    if (w && w.perceived_wait_avg != null) {
      const avg = w.perceived_wait_avg;
      return avg >= 20 ? t('long') : avg >= 10 ? t('medium') : t('short');
    }

    // Vérifier s'il y a vraiment des données de feedback
    const hasRealData = (w && w.feedbackTotal > 0) ||
                        (this.periodMode() === 'month' && this.qualityData() && 'feedbackCount' in this.qualityData()! && this.qualityData()!.feedbackCount! > 0);

    if (!hasRealData) {
      return 'n/a';
    }

    const courte = this.getWaitingPercentage('courte');
    const moyenne = this.getWaitingPercentage('moyenne');
    const longue = this.getWaitingPercentage('longue');

    const max = Math.max(courte, moyenne, longue);
    if (max === 0) return 'n/a';
    if (max === courte) return t('short');
    if (max === moyenne) return t('medium');
    return t('long');
  }

  getWaitingPerceptionColor() {
    const w = this.data();
    if (w && w.perceived_wait_avg != null) {
      const avg = w.perceived_wait_avg;
      return avg >= 20 ? '#EF4444' : avg >= 10 ? '#F59E0B' : '#10B981';
    }

    // Couleur de la plus grande valeur
    if (w && w.feedbackTotal > 0) {
      const courte = this.getWaitingPercentage('courte');
      const moyenne = this.getWaitingPercentage('moyenne');
      const longue = this.getWaitingPercentage('longue');

      const max = Math.max(courte, moyenne, longue);
      if (max === courte) return '#10B981'; // Vert
      if (max === moyenne) return '#F59E0B'; // Orange
      return '#EF4444'; // Rouge
    }

    return '#64748B'; // Gris par défaut
  }

  getWaitingBadgeClass() {
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.waitPerceptionAvg) {
      const perception = analytics.globalMetrics.waitPerceptionAvg;
      return perception === 'courte' ? 'excellence' : perception === 'moyenne' ? 'warning' : 'critical';
    }

    const w = this.data();
    if (w && w.perceived_wait_avg != null) {
      const avg = w.perceived_wait_avg;
      return avg >= 20 ? 'critical' : avg >= 10 ? 'warning' : 'good';
    }

    const hasData = (w && w.feedbackTotal > 0) ||
                   (this.periodMode() === 'month' && this.qualityData() && 'feedbackCount' in this.qualityData()! && this.qualityData()!.feedbackCount! > 0);

    if (hasData) {
      const courte = this.getWaitingPercentage('courte');
      const moyenne = this.getWaitingPercentage('moyenne');
      const longue = this.getWaitingPercentage('longue');

      const max = Math.max(courte, moyenne, longue);
      if (max === 0) return '';

      if (max === courte) {
        return courte >= 85 ? 'excellence' : 'good';
      }
      if (max === moyenne) {
        return 'warning';
      }
      return 'critical';
    }

    return '';
  }

  getWaitingBadgeText() {
    const t = (k: string) => this.translate.instant('qualite.status.' + k);
    // Utiliser les données analytics si disponibles
    const analytics = this.analyticsData();
    if (analytics?.globalMetrics?.waitPerceptionAvg) {
      const perception = analytics.globalMetrics.waitPerceptionAvg;
      return perception === 'courte' ? t('excellent') : perception === 'moyenne' ? t('needsImprovement') : t('critical');
    }

    const w = this.data();
    if (w && w.perceived_wait_avg != null) {
      const avg = w.perceived_wait_avg;
      return avg >= 20 ? t('critical') : avg >= 10 ? t('toMonitor') : t('good');
    }

    const hasData = (w && w.feedbackTotal > 0) ||
                   (this.periodMode() === 'month' && this.qualityData() && 'feedbackCount' in this.qualityData()! && this.qualityData()!.feedbackCount! > 0);

    if (hasData) {
      const courte = this.getWaitingPercentage('courte');
      const moyenne = this.getWaitingPercentage('moyenne');
      const longue = this.getWaitingPercentage('longue');

      const max = Math.max(courte, moyenne, longue);
      if (max === 0) return '';

      if (max === courte) {
        return courte >= 85 ? t('excellence') : t('good');
      }
      if (max === moyenne) {
        return t('needsImprovement');
      }
      return t('toMonitor');
    }

    return '';
  }

  // Méthodes pour graphiques dynamiques
  getComplaintMaxValue() {
    const data = this.complaintHistory();
    if (data.length === 0) return 4;
    const max = Math.max(...data);
    // Arrondir au multiple de 5 supérieur
    return Math.max(4, Math.ceil(max / 5) * 5);
  }

  getComplaintScaleLabel(percentage: number) {
    const maxValue = this.getComplaintMaxValue();
    return Math.round((maxValue * percentage) / 100);
  }

  getComplaintPointY(value: number) {
    const maxValue = this.getComplaintMaxValue();
    const ratio = value / maxValue;
    return 150 - (ratio * 120); // 120 = hauteur du graphique
  }

  getComplaintChartPoints() {
    const data = this.complaintHistory();
    const width = 280;

    if (this.periodMode() === 'week') {
      // Mode semaine : 6 points
      const spacing = width / 5;
      return data.map((value, i) => `${15 + i * spacing},${this.getComplaintPointY(value)}`).join(' ');
    } else {
      // Mode mois : 4 points
      const spacing = width / 3;
      return data.map((value, i) => `${15 + i * spacing},${this.getComplaintPointY(value)}`).join(' ');
    }
  }

  getParticipationBarX(index: number) {
    const width = 280;
    const barCount = this.periodMode() === 'week' ? 6 : 4;
    const barWidth = Math.min(30, width / barCount - 5);
    const spacing = width / barCount;
    return 15 + index * spacing + (spacing - barWidth) / 2;
  }

  getParticipationBarWidth() {
    const width = 280;
    const barCount = this.periodMode() === 'week' ? 6 : 4;
    return Math.min(30, width / barCount - 5);
  }

  getComplaintPointX(index: number) {
    const width = 280;

    if (this.periodMode() === 'week') {
      // Mode semaine : 6 points
      const spacing = width / 5; // 5 intervalles pour 6 points
      return 15 + index * spacing;
    } else {
      // Mode mois : 4 points
      const spacing = width / 3; // 3 intervalles pour 4 points
      return 15 + index * spacing;
    }
  }

  private static readonly WEEK_DAY_CODES = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  getWeekLabels() {
    const start = this.isoWeekStart(this.year(), this.week());
    const labels = [];

    for (let i = 0; i < 6; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      const dayNum = String(day.getDate()).padStart(2, '0');
      const dayLabel = this.translate.instant('common.days.shortByCode.' + QualiteComponent.WEEK_DAY_CODES[i]);
      labels.push({ day: dayLabel, num: dayNum });
    }

    return labels;
  }

  extractLast3Months(data: number[]): number[] {
    if (data.length !== 12) return data.slice(0, 3);

    const currentMonth = this.month() - 1; // 0-based
    const result = [];

    for (let i = 2; i >= 0; i--) {
      let monthIndex = currentMonth - i;
      if (monthIndex < 0) monthIndex += 12;
      result.push(data[monthIndex] || 0);
    }

    return result;
  }

  getMonthLabels() {
    if (this.periodMode() === 'month') {
      // En mode mois, afficher les semaines
      const weekAbbr = this.translate.instant('qualite.charts.weekAbbreviation');
      return [1, 2, 3, 4].map(n => `${weekAbbr}${n}`);
    }

    // Labels des 3 derniers mois
    const currentMonth = this.month();
    const labels = [];

    for (let i = 2; i >= 0; i--) {
      let monthIndex = currentMonth - 1 - i; // 0-based
      if (monthIndex < 0) monthIndex += 12;
      labels.push(this.translate.instant('common.months.shortByIndex.' + monthIndex));
    }

    return labels;
  }

  getWaitingPercentage(type: 'courte' | 'moyenne' | 'longue') {
    // Mode mois : vérifier d'abord s'il y a des données réelles
    if (this.periodMode() === 'month') {
      const q = this.qualityData();
      if (!q || !('feedbackCount' in q) || !q.feedbackCount || q.feedbackCount === 0) {
        return 0;
      }
      const total = q.feedbackCount;
      switch (type) {
        case 'courte': return Math.round(((q.feedbackLt10 || 0) / total) * 100);
        case 'moyenne': return Math.round(((q.feedback10_20 || 0) / total) * 100);
        case 'longue': return Math.round(((q.feedbackGt20 || 0) / total) * 100);
      }
    }

    // Mode semaine : vérifier s'il y a des données réelles
    const w = this.data();
    if (!w || !w.feedbackTotal || w.feedbackTotal === 0) {
      return 0;
    }
    
    const total = w.feedbackTotal;
    switch (type) {
      case 'courte': return Math.round((w.feedbackLt10 / total) * 100);
      case 'moyenne': return Math.round((w.feedback10_20 / total) * 100);
      case 'longue': return Math.round((w.feedbackGt20 / total) * 100);
    }
    return 0;
  }

  getPerceivedWaitAvg() {
    const w = this.data();
    if (w && w.perceived_wait_avg != null) {
      return Math.round(w.perceived_wait_avg) + ' min';
    }

    // Vérifier s'il y a des données de feedback (semaine ou mois)
    const hasData = (w && w.feedbackTotal > 0) ||
                   (this.periodMode() === 'month' && this.qualityData() && 'feedbackCount' in this.qualityData()! && this.qualityData()!.feedbackCount! > 0);

    if (hasData) {
      const courte = this.getWaitingPercentage('courte');
      const moyenne = this.getWaitingPercentage('moyenne');
      const longue = this.getWaitingPercentage('longue');

      const max = Math.max(courte, moyenne, longue);
      return max + '%';
    }

    return 'n/a';
  }

  getPerceivedWaitAvgMinutes() {
    const w = this.data() as any;
    if (w && (w.perceivedWaitAvg != null || w.perceived_wait_avg != null)) {
      const value = w.perceivedWaitAvg ?? w.perceived_wait_avg;
      return Math.round(value!) + ' min';
    }

    return 'n/a';
  }

  getWaitingChartAngles() {
    // Utiliser les données historiques du mois sélectionné
    const history = this.waitingPerceptionHistory();
    const currentMonth = this.month() - 1; // 0-based

    if (history.courte && history.courte.length > currentMonth) {
      const courte = history.courte[currentMonth] || 0;
      const moyenne = history.moyenne[currentMonth] || 0;
      const longue = history.longue[currentMonth] || 0;

      console.log(`Month ${this.month()} data:`, { courte, moyenne, longue });

      // Si pas de données, ne pas afficher de segments
      if (courte === 0 && moyenne === 0 && longue === 0) {
        return { courte: 0, moyenne: 0, longue: 0 };
      }

      const courteAngle = (courte / 100) * 360;
      const moyenneAngle = (moyenne / 100) * 360;
      const longueAngle = (longue / 100) * 360;

      return { courte: courteAngle, moyenne: moyenneAngle, longue: longueAngle };
    }

    // Fallback
    const w = this.data();
    if (!w || w.feedbackTotal <= 0) {
      return { courte: 0, moyenne: 0, longue: 0 };
    }

    const total = w.feedbackTotal;
    const courteAngle = (w.feedbackLt10 / total) * 360;
    const moyenneAngle = (w.feedback10_20 / total) * 360;
    const longueAngle = (w.feedbackGt20 / total) * 360;

    return { courte: courteAngle, moyenne: moyenneAngle, longue: longueAngle };
  }

  getWaitingChartPath(startAngle: number, endAngle: number) {
    const centerX = 150, centerY = 100, radius = 80;
    const start = (startAngle - 90) * Math.PI / 180;
    const end = (endAngle - 90) * Math.PI / 180;

    const x1 = centerX + radius * Math.cos(start);
    const y1 = centerY + radius * Math.sin(start);
    const x2 = centerX + radius * Math.cos(end);
    const y2 = centerY + radius * Math.sin(end);

    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  }

  /** Pourcentages par tranche sur les réponses */
  fbPercentages = computed(() => {
    const w = this.data();
    if (!w || !w.feedbackTotal) return { lt10: 0, m10_20: 0, gt20: 0 };
    const total = w.feedbackTotal;
    const pct = (v: number) => Math.round((v / total) * 100);
    return {
      lt10: pct(w.feedbackLt10),
      m10_20: pct(w.feedback10_20),
      gt20: pct(w.feedbackGt20),
    };
  });

  /** Angles pour le camembert des retours */
  fbSliceAngles = computed(() => {
    const w = this.data();
    if (!w || !w.feedbackTotal) return [];
    const vals = [w.feedbackLt10, w.feedback10_20, w.feedbackGt20];
    let acc = -Math.PI / 2;
    return vals.map(v => {
      const angle = (v / w.feedbackTotal) * Math.PI * 2;
      const start = acc;
      const end = acc + angle;
      acc = end;
      return { start, end, v };
    });
  });

  /** Path SVG d’un secteur de camembert (réutilisable) */
  getPiePath(start: number, end: number, r = 92): string {
    const large = (end - start) > Math.PI ? 1 : 0;
    const sx = Math.cos(start) * r, sy = Math.sin(start) * r;
    const ex = Math.cos(end) * r,   ey = Math.sin(end) * r;
    return `M 0 0 L ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey} Z`;
  }

  // Ferme au clic en dehors
  @HostListener('document:click')
  onDocClick(){
    if (this.yearOpen() || this.weekOpen() || this.monthOpen()) {
      this.yearOpen.set(false);
      this.weekOpen.set(false);
      this.monthOpen.set(false);
    }
  }

  openSatisfactionSubpage() {
    this.showSatisfactionSubpage.set(true);
  }

  closeSatisfactionSubpage() {
    this.showSatisfactionSubpage.set(false);
  }

  openObjectifsSubpage() {
    this.showObjectifsSubpage.set(true);
  }

  closeObjectifsSubpage() {
    this.showObjectifsSubpage.set(false);
  }

  openRapportSubpage() {
    this.showRapportSubpage.set(true);
  }

  closeRapportSubpage() {
    this.showRapportSubpage.set(false);
  }

  private pushUrl(y: number, w: number) {
    this.router.navigate([], {
      queryParams: { year: y, week: w },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public tryFetch() {
    const est = this.session.selectedId();
    if (est) this.fetch(est, this.year(), this.week(), this.month());
  }

  private resetAllData() {
    this.data.set(null);
    this.qualityData.set(null);
    this.participationData.set(null);
    this.analyticsData.set(null);
    this.weekHasData.set({});
    this.monthHasData.set({});
    this.loading.set(true);
    this.error.set(null);
    this.loadingAvailability.clear();
  }

  private loadEstablishmentData(establishmentId: string) {
    if (this.currentEstablishmentId === establishmentId && this.pendingOperations > 0) {
      return;
    }

    this.currentEstablishmentId = establishmentId;
    this.pendingOperations = 0;

    const y = (() => {
      const v = this.year();
      if (Number.isFinite(v) && v >= 2020 && v <= 2030) return v;
      const cur = new Date().getFullYear();
      this.year.set(cur);
      return cur;
    })();

    if (!this.useOrgWideView()) {
      this.loadWeekAvailability(establishmentId, y);
      this.loadMonthAvailability(establishmentId, y);
    } else {
      this.loadOrgWeekAvailability(y);
      this.loadOrgMonthAvailability(y);
    }

    setTimeout(() => {
      if (this.currentEstablishmentId === establishmentId) {
        this.fetch(establishmentId, this.year(), this.week(), this.month());
      }
    }, 200);
  }

  private fetch(establishmentId: string, isoYear: number, isoWeek: number, month?: number) {
    if (this.fetchInProgress) {
      return;
    }

    if (!establishmentId || !Number.isFinite(isoYear) || !Number.isFinite(isoWeek)) {
      this.loading.set(false);
      this.error.set('Paramètres invalides');
      return;
    }

    if (this.periodMode() === 'month' && month && (!Number.isFinite(month) || month < 1 || month > 12)) {
      this.loading.set(false);
      this.error.set('Mois invalide');
      return;
    }

    this.fetchInProgress = true;
    this.loading.set(true);
    this.error.set(null);

    if (this.MOCK_MODE) {
      const mockData = this.getMockData(establishmentId, isoYear, isoWeek, month);
      this.data.set(mockData.qualityWeek as any);
      this.qualityData.set(mockData.qualityMonth as any);
      this.loading.set(false);
      this.fetchInProgress = false;
      return;
    }

    if (this.periodMode() === 'month' && month) {
      // Supprimé: appel redondant getMonthlyQuality car analyticsData contient les mêmes données
      this.loading.set(false);
      this.fetchInProgress = false;
    } else {
      this.loading.set(false);
      this.fetchInProgress = false;
    }

    if (!this.useOrgWideView()) {
      this.loadParticipationData(establishmentId, isoYear, isoWeek, month);
    }

    let dateStr: string;
    if (this.periodMode() === 'month' && month) {
      dateStr = `${isoYear}-${String(month).padStart(2, '0')}-01`;
    } else {
      const weekStart = this.isoWeekStart(isoYear, isoWeek);
      dateStr = weekStart.toISOString().split('T')[0];
    }

    const analyticsCall = !this.useOrgWideView()
      ? this.api.getAnalytics(establishmentId, this.periodMode(), dateStr)
      : this.api.getOrgAnalytics(this.periodMode(), dateStr);

    analyticsCall.subscribe({
      next: (d: any) => {
        this.analyticsData.set(d);
      },
      error: () => {
        this.analyticsData.set(null);
      }
    });
  }

  /** Export CSV des actions manuelles (queue_events) sur la semaine sélectionnée */
  exportManual() {
    const est = this.session.selectedId();
    if (!est) return;
    const y = this.year();
    const w = this.week();

    this.api.exportManualActions(est, y, w).subscribe({
      next: (blob: Blob) => this.saveBlob(blob, `manual-actions_${y}-W${w}.csv`),
      error: (err: unknown) => { console.error(err); console.warn('Export indisponible pour cette semaine'); }
    });
  }

  /** Export CSV des retours post-visite sur la semaine sélectionnée */
  exportFeedback() {
    const est = this.session.selectedId();
    if (!est) return;
    const y = this.year();
    const w = this.week();

    this.api.exportFeedback(est, y, w).subscribe({
      next: (blob: Blob) => this.saveBlob(blob, `feedback_${y}-W${w}.csv`),
      error: (err: unknown) => { console.error(err); console.warn('Export indisponible pour cette semaine'); }
    });
  }

  /** Export du rapport qualité synthèse (PDF mensuel IWEA) */
  exportQualityReport() {
    const est = this.session.selectedId();
    if (!est) return;

    // On déduit le mois à partir du début de la semaine ISO sélectionnée
    const start = this.isoWeekStart(this.year(), this.week());
    const ymYear = start.getUTCFullYear();
    const ymMonth = start.getUTCMonth() + 1; // 0-based -> 1..12

    this.api.downloadMonthlyRaqPdf(est, ymYear, ymMonth, /* refresh */ true).subscribe({
      next: (resp: HttpResponse<Blob>) => {
        const blob = resp.body!;
        // Essayer de récupérer le nom de fichier depuis le header Content-Disposition
        const cd = resp.headers.get('Content-Disposition') || '';
        const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(cd);
        const suggested = match ? decodeURIComponent(match[1]) : `Fiche-RAQ-IWEA_${ymYear}-${String(ymMonth).padStart(2, '0')}_${est}.pdf`;
        this.saveBlob(blob, suggested);
      },
      error: (err: any) => {
        console.error(err);
        // Ne pas masquer toute la page pour une erreur d'export
        console.warn('Export IWEA indisponible pour cette période');
      }
    });
  }

  /** Export du rapport qualité complet pour la semaine sélectionnée (placeholder HTML) */
  async exportFullQualityReport() {
    const est = this.session.selectedId();
    if (!est) return;
    const y = this.year();
    const w = this.week();

    try {
      // Charger le fichier HTML
      const response = await fetch('/assets/rapport_mensuel_complet.html');
      const htmlContent = await response.text();

      // Créer un blob avec le contenu HTML
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);

      // Télécharger le fichier
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-qualite-complet_${y}-W${w}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du chargement du rapport:', error);
      console.warn('Erreur lors de la génération du rapport');
    }
  }

  private saveBlob(blob: Blob, filename: string){
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // --- helpers UI ---
  currentIsoWeek(): number {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
    const week = Math.ceil((days + start.getDay() + 1) / 7);
    return Math.max(1, Math.min(53, week));
  }

  private isoWeekStart(y: number, w: number): Date {
    const jan4 = new Date(Date.UTC(y, 0, 4));
    const jan4Day = (jan4.getUTCDay() + 6) % 7;
    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day);
    const monday = new Date(week1Monday);
    monday.setUTCDate(week1Monday.getUTCDate() + (w - 1) * 7);
    return new Date(Date.UTC(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate()));
  }
  private addDays(d: Date, n: number) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

  private formatDayMonth(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = this.translate.instant('common.months.longByIndex.' + date.getMonth());
    return `${day} ${month}`;
  }

  weekLabel = computed(() => {
    const start = this.isoWeekStart(this.year(), this.week());
    const end = this.addDays(start, 6);
    return `${this.formatDayMonth(start)} → ${this.formatDayMonth(end)}`;
  });

  years(): number[] {
    const y = new Date().getFullYear();
    return [y, y - 1];
  }
  weeks(): number[] {
    const map = this.weekHasData();
    // Si aucune donnée disponible, retourner liste vide
    if (!map || Object.keys(map).length === 0) return [];

    const currentWeek = this.currentIsoWeek();
    const currentYear = new Date().getFullYear();
    const selectedYear = this.year();

    let allWeeks: number[];
    if (selectedYear < currentYear) {
      allWeeks = Array.from({ length: 53 }, (_, i) => 53 - i);
    } else {
      allWeeks = Array.from({ length: currentWeek }, (_, i) => currentWeek - i);
    }

    // Filtrer pour ne garder que les semaines avec données
    return allWeeks.filter(w => this.isWeekEnabled(w));
  }

  totalPoints = computed(() => {
    const w = this.data();
    return w ? w.distLt10 + w.dist10_20 + w.distGt20 : 0;
  });

  /** Indique si des valeurs publiées existent pour la semaine (utilisé pour afficher n/a) */
  hasPublishedData(): boolean {
    return !!this.data() && this.totalPoints() > 0;
  }

  /** Indique s'il y a des données (publiées ou feedback) pour afficher les blocs */
  hasAnyData(): boolean {
    const w = this.data();
    if (!w) return false;

    // Vérifier s'il y a des données d'attente
    const hasWaitData = this.totalPoints() > 0;

    // Vérifier s'il y a des données de feedback
    const hasFeedbackData = (w.feedbackTotal ?? 0) > 0;

    // Vérifier s'il y a des données de tendance
    const hasTrendData = w.trendVsPrev != null;

    const result = hasWaitData || hasFeedbackData || hasTrendData;

    return result;
  }

  distSlices = computed<Slice[]>(() => {
    const w = this.data(); if (!w) return [];
    return [
      { name: '< 10', value: w.distLt10 },
      { name: '10–20', value: w.dist10_20 },
      { name: '> 20', value: w.distGt20 },
    ];
  });

  fbSlices = computed<Slice[]>(() => {
    const w = this.data(); if (!w) return [];
    return [
      { name: '< 10', value: w.feedbackLt10 },
      { name: '10–20', value: w.feedback10_20 },
      { name: '> 20', value: w.feedbackGt20 },
    ];
  });

  objectiveRateV1 = computed(() => {
    const total = this.totalPoints(); const w = this.data();
    if (!w || !total) return 0;
    return Math.round(((w.distLt10 + w.dist10_20) / total) * 100);
  });

  trendText = computed(() => {
    const w = this.data();
    if (!w || w.trendVsPrev == null) return { txt: 'n/a', cls: 'muted' as const };
    const v = Math.round(w.trendVsPrev);
    return { txt: (v > 0 ? '+' : '') + v + ' min', cls: v > 0 ? ('red' as const) : v < 0 ? ('green' as const) : ('muted' as const) };
  });

  getDonutPath(value: number, total: number, r = 34, cx = 40, cy = 40) {
    if (total <= 0) return '';
    const a = (value / total) * Math.PI * 2;
    const x = cx + Math.sin(a) * r;
    const y = cy - Math.cos(a) * r;
    const large = a > Math.PI ? 1 : 0;
    return `M ${cx} ${cy - r} A ${r} ${r} 0 ${large} 1 ${x} ${y}`;
  }

  // Méthodes Math pour les templates
  Math = Math;

  mathRound(value: number): number {
    return Math.round(value);
  }

  mathRoundDecimal(value: number): number {
    return Math.round(value * 10) / 10;
  }

  getEstablishmentName(id: string): string {
    const establishment = this.session.establishments().find(e => e.id === id);
    return establishment?.name || 'Établissement';
  }

  refreshData(): void {
    const estId = this.session.selectedId();
    if (estId) {
      this.isRefreshing.set(true);
      this.api.refreshEstablishmentData(estId).subscribe({
        next: () => {
          this.resetAllData();
          this.loadEstablishmentData(estId);
          setTimeout(() => this.isRefreshing.set(false), 1500);
        },
        error: () => {
          this.isRefreshing.set(false);
        }
      });
    }
  }

  setActiveTab(tab: 'objectifs' | 'communication' | 'formulaire') {
    this.activeTab.set(tab);
    this.showCommunicationSubpage.set(tab === 'communication');
    this.showFormulaireSubpage.set(tab === 'formulaire');
  }
}
